/**
 * Development-friendly logging utility
 *
 * Provides structured logging with:
 * - Conditional logging based on environment
 * - Log levels (info, warn, error, debug)
 * - Structured context data
 * - Production-ready error tracking
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/lib/logger';
 *
 * logger.info('User logged in', { userId: '123' });
 * logger.error('Failed to fetch orders', error);
 * logger.debug('Component rendered', { props });
 * ```
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;

  /**
   * Log informational messages (development only)
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Log warning messages (development only)
   */
  warn(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, context || '');
    }
  }

  /**
   * Log error messages (always logged)
   * In production, should be sent to error tracking service
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    if (this.isDevelopment) {
      console.error(`[ERROR] ${message}`, {
        error: errorObj,
        stack: errorObj.stack,
        ...context,
      });
    }

    if (this.isProduction) {
      // In production, send to error tracking service
      // Example: Sentry, LogRocket, DataDog, etc.
      // Sentry.captureException(errorObj, { extra: context });

      // For now, log to console in production (can be filtered by log aggregators)
      console.error(`[ERROR] ${message}`, {
        message: errorObj.message,
        ...context,
      });
    }
  }

  /**
   * Log debug messages (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${message}`, context || '');
    }
  }

  /**
   * Log API requests (development only)
   */
  api(method: string, url: string, data?: unknown): void {
    if (this.isDevelopment) {
      console.log(`[API] ${method.toUpperCase()} ${url}`, data || '');
    }
  }

  /**
   * Log performance metrics (development only)
   */
  perf(label: string, duration: number): void {
    if (this.isDevelopment) {
      console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
    }
  }

  /**
   * Create a timer for performance measurement
   */
  time(label: string): () => void {
    const start = performance.now();

    return () => {
      const duration = performance.now() - start;
      this.perf(label, duration);
    };
  }

  /**
   * Log component lifecycle (development only)
   */
  component(name: string, action: 'mount' | 'unmount' | 'update', props?: unknown): void {
    if (this.isDevelopment) {
      console.log(`[COMPONENT] ${name} ${action}`, props || '');
    }
  }
}

// Export singleton instance
export const logger = new Logger();

/**
 * Helper to wrap async functions with error logging
 */
export function withErrorLogging<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: string
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      logger.error(`${context} failed`, error);
      throw error;
    }
  }) as T;
}

/**
 * Helper to log function execution time
 */
export function withPerfLogging<T extends (...args: any[]) => any>(
  fn: T,
  label: string
): T {
  return ((...args: Parameters<T>) => {
    const endTimer = logger.time(label);
    try {
      const result = fn(...args);

      // Handle promises
      if (result instanceof Promise) {
        return result.finally(endTimer);
      }

      endTimer();
      return result;
    } catch (error) {
      endTimer();
      throw error;
    }
  }) as T;
}
